import React, { createContext, useContext } from 'react';
import useKemuHubLink from '@hooks/useHubLink';
import useHubServices from '@hooks/useHubServices';
import { HubConnectionStatus, HubConnectorWithWebAppMethods } from '@src/types/kemuHub/hub_t';
import { GetServicesResponse } from '@kemu-io/hs-types';

type GetServicesResponseInstance = GetServicesResponse & {
	isDefault?: boolean;
}

export type HubContextType = {
	/**
	 * Hub connection information
	 */
	connector: HubConnectorWithWebAppMethods;
	online: boolean;
	status: HubConnectionStatus;
	/**
	 * Hub services information
	 */
	services: GetServicesResponseInstance;
	isReady: boolean;
	forceRefresh: () => void;
}

export const HubContext = createContext<HubContextType | null>(null);

interface Props {
	children: React.ReactNode;
}

/**
 * Context provider that exposes hub connection and services data
 * Combines useKemuHubLink and useHubServices hooks
 */
const HubContextProvider = (props: Props): React.JSX.Element => {
	const hubLink = useKemuHubLink();
	const hubServices = useHubServices(hubLink.connector, hubLink.status);

	const contextValue: HubContextType = {
		...hubLink,
		...hubServices,
	};

	return (
		<HubContext.Provider value={contextValue}>
			{props.children}
		</HubContext.Provider>
	);
};

/**
 * Hook to access the hub context
 * @returns The hub context value
 * @throws Error if used outside of HubContextProvider
 */
export const useHubContext = (): HubContextType => {
	const context = useContext(HubContext);
	if (!context) {
		throw new Error('useHubContext must be used within a HubContextProvider');
	}
	return context;
};

export default HubContextProvider;
